import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { pingOnboardingApi, type PingOnboardingStatus } from '../services/api';

const knownStatuses = new Set(['NOT_STARTED', 'CREATED', 'PENDING', 'APPROVED', 'DENIED', 'UNVERIFIED', 'PROCESSING', 'EXPIRED']);
const accountStatuses = new Set(['missing', 'confirmed', 'wrong_currency', 'review_required', 'unknown']);
const checkedStatus = (value: PingOnboardingStatus): PingOnboardingStatus => {
  if (!value || typeof value.enabled !== 'boolean' || !Array.isArray(value.contexts) ||
    value.contexts.some(context => !context || typeof context.id !== 'string' || typeof context.mode !== 'string') ||
    (value.enabled && typeof value.ready !== 'boolean')) {
    throw new Error('Invalid Ping onboarding response');
  }
  return value;
};
export function PingOnboardingPanel({ fee = false, showInactive = false }: { fee?: boolean; showInactive?: boolean }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<PingOnboardingStatus | null>(null);
  const [contextId, setContextId] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const pending = useRef(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { setStatus(checkedStatus(await pingOnboardingApi.status(fee, contextId))); setError(false); }
    catch { setStatus(previous => previous ? { ...previous, ready: false, canSubmit: false, merchantStatus: 'UNKNOWN', kycStatus: 'UNKNOWN', accountStatus: 'unknown', accountMasked: null } : null); setError(true); }
    finally { setBusy(false); }
  }, [fee, contextId]);
  useEffect(() => { void load(); }, [load]);
  const start = async () => {
    if (pending.current) return;
    pending.current = true; setBusy(true); setError(false);
    try { setStatus(checkedStatus(await pingOnboardingApi.start(fee, contextId))); }
    catch { setError(true); await load(); setError(true); }
    finally { pending.current = false; setBusy(false); }
  };
  if (status && !status.enabled && !status.contexts.length) return showInactive ? <p className="text-muted mb-0">{t('pingOnboarding.inactive')}</p> : null;
  const key = (value?: string) => knownStatuses.has(value ?? '') ? value : 'UNKNOWN';
  return <section className="ping-onboarding text-start" aria-label={t(fee ? 'pingOnboarding.feeTitle' : 'pingOnboarding.title')}>
    <h4>{t(fee ? 'pingOnboarding.feeTitle' : 'pingOnboarding.title')}</h4>
    <p>{t(fee ? 'pingOnboarding.feeDescription' : 'pingOnboarding.description')}</p>
    {status && status.contexts.length > 0 && <div className="mb-3">
      <label className="form-label" htmlFor={`ping-context-${fee}`}>{t('pingOnboarding.context')}</label>
      <select id={`ping-context-${fee}`} className="form-select form-select-lg" value={contextId ?? ''} disabled={busy}
        onChange={event => { setStatus(null); setContextId(event.target.value || undefined); }}>
        <option value="">{t('pingOnboarding.currentContext')}</option>
        {status.contexts.map((context, index) => <option key={context.id} value={context.id}>
          {t('pingOnboarding.savedContext', { number: index + 1, mode: t(`pingOnboarding.modes.${context.mode === 'mock' ? 'mock' : 'sandbox'}`) })}
        </option>)}
      </select>
    </div>}
    {status?.enabled && <>
      <p className="alert alert-info">{t('pingOnboarding.testScope')}</p>
      <dl className="row g-2">
        <dt className="col-12 col-sm-5">{t('pingOnboarding.merchant')}</dt>
        <dd className="col-12 col-sm-7">{t(`pingOnboarding.statuses.${key(status.merchantStatus)}`)}</dd>
        <dt className="col-12 col-sm-5">{t('pingOnboarding.kyc')}</dt>
        <dd className="col-12 col-sm-7">{t(`pingOnboarding.statuses.${status.renewalRequired ? 'EXPIRED' : key(status.kycStatus)}`)}</dd>
        <dt className="col-12 col-sm-5">{t('pingOnboarding.account')}</dt>
        <dd className="col-12 col-sm-7">{t(`pingOnboarding.accounts.${status.accountStatus === 'confirmed' && status.mode === 'mock' ? 'confirmedMock' : accountStatuses.has(status.accountStatus ?? '') ? status.accountStatus : 'unknown'}`)}
          {status.accountMasked && <span className="d-block">{status.accountMasked}</span>}
        </dd>
      </dl>
      {!!status.missingFields?.length && <div className="alert alert-warning">
        <p>{t('pingOnboarding.incomplete')}</p>
        <ul>{status.missingFields.map(field => <li key={field}>{t(`pingOnboarding.fields.${field}`)}</li>)}</ul>
        {!fee && <Link className="btn btn-outline-primary btn-lg w-100" to="/settings/company?tab=overview">{t('pingOnboarding.completeProfile')}</Link>}
      </div>}
      {(status.renewalRequired || ['DENIED', 'EXPIRED'].includes(status.kycStatus ?? '') || status.merchantStatus === 'DENIED' || ['wrong_currency', 'review_required'].includes(status.accountStatus ?? '')) && <p className="alert alert-warning">{t('pingOnboarding.providerReview')}</p>}
      {status.errorCode && <p className="alert alert-warning" role="alert">
        {t(status.errorCode === 'ping_onboarding_rejected' ? 'pingOnboarding.rejected' : 'pingOnboarding.uncertain')}
      </p>}
      {status.ready && <p className="alert alert-success" role="status">{t(status.mode === 'mock' ? 'pingOnboarding.readyMock' : 'pingOnboarding.ready')}</p>}
      {!status.ready && <p>{t('pingOnboarding.testAccount')}</p>}
    </>}
    {error && <p className="alert alert-warning" role="alert">{t('pingOnboarding.loadError')}</p>}
    <div className="d-grid gap-2 d-sm-flex">
      {status?.enabled && !status.ready && <button type="button" className="btn btn-primary btn-lg"
        disabled={busy || !status.canSubmit || !!status.missingFields?.length} onClick={() => void start()}>
        {t('pingOnboarding.start')}
      </button>}
      <button type="button" className="btn btn-outline-primary btn-lg" disabled={busy} onClick={() => void load()}>
        {t(busy ? 'pingOnboarding.loading' : 'pingOnboarding.refresh')}
      </button>
    </div>
    <p className="text-muted small mt-3 mb-0">{t(status?.mode === 'mock' ? 'pingOnboarding.privacyMock' : fee ? 'pingOnboarding.privacyFee' : 'pingOnboarding.privacy')}</p>
  </section>;
}
